import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { ErrorHandlingService } from '../../error-handling/error-handling.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { SpinnerService } from '../../spinner';
import { Observable, of } from 'rxjs';
import { HttpHeaderConstants } from '../../http-header.constant';
import { catchError, finalize } from 'rxjs/operators';
import { ResponsysInstall } from '../../../models/appcloud/responsys-install.model';

/**
 * @deprecated
 * Use {@link AppInstallService} instead and rely on the responsysInstallDetail property
 */
@Injectable({
    providedIn: 'root',
})
export class ResponsysInstallDetailService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private RESPONSYS_INSTALL_URL: string = environment.publicApiHost + '/appcloud/responsys-installs';

    getByInstallUuid(installUuid: string, additionalHeaders: any = {}): Observable<ResponsysInstall[]> {
        this.#spinnerService.addProcess(
            'ResponsysInstallService.getByInstallUuid.' + installUuid,
            'Loading your configuration...'
        );
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('uuid', installUuid),
        };

        return this.#http.get<ResponsysInstall[]>(this.RESPONSYS_INSTALL_URL, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving app data from Relationship One. Please try again later.'
                );
                return of<ResponsysInstall[]>();
            }),
            finalize(() =>
                this.#spinnerService.completeProcess('ResponsysInstallService.getByInstallUuid.' + installUuid)
            )
        );
    }

    create<T>(responsysInstall: ResponsysInstall, additionalHeaders: any = {}): Observable<ResponsysInstall> {
        this.#spinnerService.addProcess('ResponsysInstallService.create', 'Creating Responsys install...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.post<ResponsysInstall>(this.RESPONSYS_INSTALL_URL, responsysInstall, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while creating Responsys install. Please try again later.'
                );
                return of<ResponsysInstall>();
            }),
            finalize(() => this.#spinnerService.completeProcess('ResponsysInstallService.create'))
        );
    }

    update<T>(
        id: number,
        responsysInstall: ResponsysInstall,
        additionalHeaders: any = {}
    ): Observable<ResponsysInstall> {
        this.#spinnerService.addProcess('ResponsysInstallService.update', 'Updating Responsys install...');
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };

        return this.#http
            .patch<ResponsysInstall>(this.RESPONSYS_INSTALL_URL + '/' + id, responsysInstall, httpOptions)
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while updating Responsys install. Please try again later.'
                    );
                    return of<ResponsysInstall>();
                }),
                finalize(() => this.#spinnerService.completeProcess('ResponsysInstallService.update'))
            );
    }
}
